import { PageAttr } from "../../../../shared/domain/entities/fetch-entities";
import { pickBy } from "lodash";
import { useConcatPages } from "../../../../shared/infrastructure";
import { useFetch } from "../../../../public/auth/infrastructure/store/fetch-context";
import type { UseGetUsersFilter, UserT } from "../../domain";

import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useInfiniteQuery,
} from "@tanstack/react-query";

const usersKeys = {
  all: ["users"] as const,
  users: () => [...usersKeys.all, "users-list"] as const,
  usersList: (filters: UseGetUsersFilter | undefined) =>
    [...usersKeys.users(), filters] as const,
};

const USERS_ENDPOINT = "users";

const useInfiniteGetUsers = (
  filters?: UseGetUsersFilter,
  config?: UseInfiniteQueryOptions<PageAttr<UserT>>
): UseInfiniteQueryResult<PageAttr<UserT>> & {
  allPages: PageAttr<UserT>["data"];
} => {
  const fetch = useFetch();

  const response = useInfiniteQuery<PageAttr<UserT>>(
    usersKeys.usersList(filters),
    async ({ pageParam: page = 1 }: { pageParam?: number }) => {
      const response = await fetch.get<PageAttr<UserT>>(USERS_ENDPOINT, {
        params: {
          page,
          limit: 10,
          ...pickBy(filters),
        },
      });

      return response.data;
    },
    {
      ...config,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.data.length === lastPage.pagination.limit
          ? allPages.length + 1
          : undefined,
    }
  );

  const allPages = useConcatPages(response.data?.pages ?? []);

  return {
    ...response,
    allPages,
  };
};

export { useInfiniteGetUsers };
